import React, { useState, useEffect } from "react";
import { BlogCategory, BlogSearchParams } from "@/types/blog";
import { FormInput, DropdownMenu } from "@/components/bnp-ui";

interface BlogFiltersProps {
  categories: BlogCategory[];
  onFilterChange: (filters: BlogSearchParams) => void;
  initialFilters?: BlogSearchParams;
}

const BlogFilters: React.FC<BlogFiltersProps> = ({
  categories,
  onFilterChange,
  initialFilters = {},
}) => {
  const [searchQuery, setSearchQuery] = useState(initialFilters.query || "");
  const [selectedCategory, setSelectedCategory] = useState(
    initialFilters.category || "",
  );
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Trigger filter change when debounced search or category changes
  useEffect(() => {
    const filters: BlogSearchParams = {};

    if (debouncedSearchQuery) {
      filters.query = debouncedSearchQuery;
    }

    if (selectedCategory) {
      filters.category = selectedCategory;
    }

    onFilterChange(filters);
  }, [debouncedSearchQuery, selectedCategory, onFilterChange]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleCategoryChange = (event: { target: { value: string } }) => {
    setSelectedCategory(event.target.value);
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setSelectedCategory("");
    setDebouncedSearchQuery("");
    onFilterChange({});
  };

  const hasActiveFilters = searchQuery || selectedCategory;

  const categoryOptions = [
    "All Categories",
    ...categories.map((cat) => cat.name),
  ];

  const getCategoryValue = () => {
    if (!selectedCategory) return "All Categories";
    const category = categories.find((cat) => cat.slug === selectedCategory);
    return category ? category.name : "All Categories";
  };

  return (
    <div className="bg-white rounded-xl border border-grey-2 p-5 md:p-6 mb-6">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
        <div className="md:col-span-7">
          <FormInput
            name="search"
            label="Search Articles"
            type="text"
            placeholder="Search by title, content, or tags..."
            value={searchQuery}
            onChange={handleSearchChange}
          />
        </div>

        <div className="md:col-span-4">
          <DropdownMenu
            name="category"
            label="Category"
            options={categoryOptions}
            value={getCategoryValue()}
            onChange={(event) => {
              const value = event.target.value;
              if (value === "All Categories") {
                handleCategoryChange({ target: { value: "" } });
              } else {
                const category = categories.find((cat) => cat.name === value);
                handleCategoryChange({
                  target: { value: category ? category.slug : "" },
                });
              }
            }}
            placeholder="Select category"
            searchable={true}
          />
        </div>

        <div className="md:col-span-1 flex justify-end">
          {hasActiveFilters && (
            <button
              onClick={handleClearFilters}
              className="w-full md:w-auto bg-grey-2 text-grey-7 px-4 py-3 rounded-lg font-semibold text-sm hover:bg-grey-3 transition-colors h-12"
              title="Clear all filters"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mx-auto"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>
      </div>

      {hasActiveFilters && (
        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <span className="text-sm text-grey-7 font-medium">
            Active filters:
          </span>
          {searchQuery && (
            <span className="inline-flex items-center gap-1 bg-main-100 text-main-900 px-3 py-1 rounded-full text-xs font-semibold">
              Search: &quot;{searchQuery}&quot;
              <button
                onClick={() => setSearchQuery("")}
                className="ml-1 hover:text-main-700"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-3 w-3"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </span>
          )}
          {selectedCategory && (
            <span className="inline-flex items-center gap-1 bg-main-100 text-main-900 px-3 py-1 rounded-full text-xs font-semibold">
              Category: {getCategoryValue()}
              <button
                onClick={() => setSelectedCategory("")}
                className="ml-1 hover:text-main-700"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-3 w-3"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default BlogFilters;
